import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authService } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';
import { useEmpresaStore } from '@/store/empresaStore';
import Button from '@/components/common/Button';
import styles from '@/styles/ui.module.css';

const loginSchema = z.object({
  username: z.string().min(1, 'El usuario es requerido'),
  password: z.string().min(1, 'La contraseña es requerida'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const clearEmpresa = useEmpresaStore((state) => state.clearEmpresa);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      setAuth(data.user, data.token);
      clearEmpresa();
      navigate('/empresas');
    },
  });

  const onSubmit = (data: LoginFormData) => {
    loginMutation.mutate(data);
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginWrapper}>
        <div className={styles.loginHero}>
          <h2>Acceso comerciales</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.loginForm}>
          <div className={styles.formGroup}>
            <span className={styles.labelText}>Usuario</span>
            <div className={styles.inputWrapper}>
              <input
                {...register('username')}
                type="text"
                className={styles.inputField}
                placeholder="ej. juan.perez"
              />
            </div>
            {errors.username && (
              <p className={styles.mutedText}>{errors.username.message}</p>
            )}
          </div>

          <div className={styles.formGroup}>
            <span className={styles.labelText}>Contraseña</span>
            <div className={styles.inputWrapper}>
              <input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                className={styles.inputField}
                placeholder="Contraseña"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className={styles.passwordToggle}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.passwordIcon}>
                    <path
                      d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.passwordIcon}>
                    <path
                      d="M3 3l18 18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10.6 6.3A11.6 11.6 0 0 1 12 6c6.5 0 10 6 10 6a18.7 18.7 0 0 1-3.2 3.8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6.7 6.8C4.1 8.2 2 12 2 12s3.5 6 10 6c1.5 0 2.8-.3 4-.8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.9 9.9A3 3 0 0 0 14.1 14.1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <p className={styles.mutedText}>{errors.password.message}</p>
            )}
          </div>

          {loginMutation.isError && (
            <div className={styles.toast}>
              <span className="material-symbols-outlined" style={{ color: '#dc2626' }}>
                error
              </span>
              <p>Credenciales inválidas. Por favor verifica tus datos.</p>
            </div>
          )}

          <div className={styles.loginFooter}>
            <button type="button" className={styles.loginForgotButton}>
              Olvidaste tu contraseña?
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="sm"
            className={styles.loginSubmitButton}
            disabled={loginMutation.isPending}
          >
            {loginMutation.isPending ? (
              <>
                <span className="material-symbols-outlined">hourglass_top</span>
                Iniciando sesión...
              </>
            ) : (
              'Iniciar Sesión'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
